import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { recordMeetingBookedOnLink } from '@/api/endpoints/meetings';
import { bookingLinkUrl, type CustomerCrmView } from '@/services/dialer/bookedMeeting';

/**
 * « Renseigner un rendez-vous pris » quand le client n'a aucun CRM branche.
 *
 * 🚨 Kevin, 2026-09-30 : il n'y a alors PAS de formulaire. Il n'y a ni agenda
 * a interroger ni CRM a qui transmettre une date, et la date se choisira dans
 * l'outil du client, pas ici. Le geste ouvre donc le lien de prise de
 * rendez-vous dans un onglet et marque le rendez-vous comme obtenu sur
 * l'appel qui l'a permis. Le formulaire precedent montrait un calendrier
 * complet surmonte d'un avertissement disant qu'aucun CRM n'etait connecte.
 */

export interface BookedOnLinkPanelProps {
  /** Le prospect avec qui le rendez-vous est pris ; null desactive le geste. */
  prospectId: string | null;
  customer: CustomerCrmView | null;
  onDone?: () => void;
}

export default function BookedOnLinkPanel({ prospectId, customer, onDone }: BookedOnLinkPanelProps) {
  const { t } = useTranslation(['dialer', 'common']);
  const [enCours, setEnCours] = useState(false);
  const lien = bookingLinkUrl(customer);

  const ouvrirEtMarquer = async () => {
    if (!prospectId || !lien) return;
    // L'onglet s'ouvre AVANT l'appel reseau : un navigateur ne laisse ouvrir
    // une fenetre que dans la foulee du clic, et l'attente d'une reponse
    // suffit a la faire bloquer.
    window.open(lien, '_blank', 'noopener,noreferrer');
    setEnCours(true);
    try {
      const resultat = await recordMeetingBookedOnLink(prospectId);
      toast.success(t(resultat.marked
        ? 'dialer:bookedMeeting.onLink.marked'
        : 'dialer:bookedMeeting.onLink.noCall'));
      onDone?.();
    } catch (err) {
      const message = (err as { message?: string })?.message || t('dialer:bookedMeeting.toast.unknownError');
      toast.error(t('dialer:bookedMeeting.toast.error', { message }));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('dialer:bookedMeeting.onLink.hint')}</p>
      <Button
        type="button"
        className="w-full"
        disabled={!prospectId || !lien || enCours}
        onClick={() => void ouvrirEtMarquer()}
      >
        {enCours
          ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          : <ExternalLink className="w-4 h-4 mr-2" />}
        {t('dialer:bookedMeeting.onLink.action')}
      </Button>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CalendarCheck className="w-3.5 h-3.5 shrink-0" />
        {t('dialer:bookedMeeting.onLink.thenMarked')}
      </p>
    </div>
  );
}
